import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Button, Divider, Chip,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Alert, Grid, LinearProgress, Fade, Avatar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';

function ScoreBar({ label, value }) {
  const color = value >= 8 ? '#10b981' : value >= 6 ? '#6366f1' : value >= 4 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>{value}/10</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value * 10}
        sx={{
          height: 8, borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 }
        }}
      />
    </Box>
  );
}

export default function FeedbackScreen({ profile, transcript, feedback, onFeedbackReceived, onRestart }) {
  const [loading, setLoading] = useState(!feedback);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' | 'transcript'

  useEffect(() => {
    if (feedback) return;

    async function fetchFeedback() {
      try {
        const res = await fetch('/api/mi/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile, transcript })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        onFeedbackReceived(data.feedback);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchFeedback();
  }, []); // eslint-disable-line

  return (
    <Fade in>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
        {/* Header */}
        <Paper elevation={0} sx={{
          px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider',
          borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="h6">Interview Complete</Typography>
            <Typography variant="caption" color="text.secondary">
              {profile?.role} at {profile?.company} — {profile?.interviewType} interview
            </Typography>
          </Box>
          <Button
            variant="outlined" size="small" startIcon={<RestartAltIcon />}
            onClick={onRestart} sx={{ borderRadius: 2 }}
          >
            New Interview
          </Button>
        </Paper>

        <Container maxWidth="md" sx={{ mt: 4 }}>
          {/* Tab switcher */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {[
              { key: 'feedback', label: 'Feedback', icon: <TipsAndUpdatesIcon fontSize="small" /> },
              { key: 'transcript', label: 'Transcript', icon: <AssignmentIcon fontSize="small" /> }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'contained' : 'outlined'}
                size="small"
                startIcon={tab.icon}
                onClick={() => setActiveTab(tab.key)}
                sx={{ borderRadius: 2 }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          {/* Transcript tab */}
          {activeTab === 'transcript' && (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Full Transcript</Typography>
              <Divider sx={{ mb: 2 }} />
              {transcript.length === 0 ? (
                <Typography color="text.secondary">No messages recorded.</Typography>
              ) : (
                transcript.map((msg, i) => (
                  <Box key={i} sx={{
                    display: 'flex', gap: 1.5, mb: 2.5,
                    alignItems: 'flex-start',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                  }}>
                    <Avatar sx={{
                      width: 32, height: 32, flexShrink: 0,
                      bgcolor: msg.role === 'assistant' ? 'secondary.main' : 'primary.main',
                      fontSize: 16
                    }}>
                      {msg.role === 'assistant' ? <SmartToyIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                    </Avatar>
                    <Box sx={{ maxWidth: '80%' }}>
                      <Typography variant="caption" color="text.secondary" sx={{
                        display: 'block', mb: 0.5,
                        textAlign: msg.role === 'user' ? 'right' : 'left'
                      }}>
                        {msg.role === 'assistant' ? (profile?.interviewerName || 'Interviewer') : 'You'}
                      </Typography>
                      <Paper elevation={0} sx={{
                        px: 2, py: 1.5,
                        bgcolor: msg.role === 'assistant' ? 'rgba(255,255,255,0.04)' : 'rgba(99,102,241,0.12)',
                        border: '1px solid', borderColor: 'divider',
                        borderRadius: msg.role === 'assistant' ? '4px 12px 12px 12px' : '12px 4px 12px 12px'
                      }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>
          )}

          {/* Feedback tab */}
          {activeTab === 'feedback' && (
            <>
              {loading && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography color="text.secondary">Analysing your interview…</Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to generate feedback: {error}
                </Alert>
              )}

              {!loading && !error && feedback && (
                <Fade in>
                  <Box>
                    {/* Overall + scores */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={7}>
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Overall Assessment</Typography>
                          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                            {feedback.overall}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scores</Typography>
                          {feedback.scores && Object.entries(feedback.scores).map(([key, val]) => (
                            <ScoreBar key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} value={val} />
                          ))}
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Strengths */}
                    {feedback.strengths?.length > 0 && (
                      <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <StarIcon sx={{ color: '#f59e0b' }} />
                          <Typography variant="subtitle1" fontWeight={600}>Strengths</Typography>
                        </Box>
                        {feedback.strengths.map((s, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
                            <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{s}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}

                    {/* Improvements */}
                    {feedback.improvements?.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <TrendingUpIcon sx={{ color: 'primary.main' }} />
                          <Typography variant="subtitle1" fontWeight={600}>Areas to Improve</Typography>
                        </Box>
                        {feedback.improvements.map((item, i) => (
                          <Accordion
                            key={i}
                            elevation={0}
                            sx={{
                              mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important',
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { borderColor: 'primary.main' }
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Chip label={item.area} size="small" color="primary" variant="outlined" />
                                <Typography variant="body2" fontWeight={500}>{item.issue}</Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Suggestion
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                                    {item.suggestion}
                                  </Typography>
                                </Box>
                                {item.betterResponse && (
                                  <Paper elevation={0} sx={{
                                    p: 2, bgcolor: 'rgba(16,185,129,0.06)',
                                    border: '1px solid rgba(16,185,129,0.2)', borderRadius: 2
                                  }}>
                                    <Typography variant="caption" color="secondary.main" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                      Example better response
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6, fontStyle: 'italic' }}>
                                      "{item.betterResponse}"
                                    </Typography>
                                  </Paper>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <Button
                        variant="contained" size="large"
                        startIcon={<RestartAltIcon />}
                        onClick={onRestart}
                        sx={{
                          borderRadius: 3, px: 4,
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          boxShadow: '0 4px 24px rgba(99,102,241,0.4)'
                        }}
                      >
                        Practice Again
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              )}
            </>
          )}
        </Container>
      </Box>
    </Fade>
  );
}
